import { EventLocationData, Undefinable } from '@eagle/react-common';
import { getTripEventsInRange, TripSummaryLike } from './trip-history-event-filtering-utils';

interface ReverseGeocodeParams {
  lat: number;
  long: number;
}

interface ReverseGeocodeResult {
  address?: unknown;
}

export type ReverseGeocodeResolver = (params: ReverseGeocodeParams) => Promise<ReverseGeocodeResult>;

export interface ThrottledReverseGeocodeResolverOptions {
  intervalMs?: number;
}

export interface ResolveTimelineAddressTextOptions {
  cache: Map<string, string>;
  enabled: boolean;
  event: Undefinable<EventLocationData>;
  inFlight?: Map<string, Promise<string>>;
  reverseGeocode: ReverseGeocodeResolver;
  unknownLabel?: string;
}

const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

export const formatLatLngText = (event?: EventLocationData, unknownLabel = '--'): string => {
  const location = event?.data?.location;
  if (!location || !isFiniteNumber(location.latitude) || !isFiniteNumber(location.longitude)) return unknownLabel;

  const { latitude, longitude } = location;
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
};

export const toCoordinateKey = (event?: EventLocationData): string | undefined => {
  const location = event?.data?.location;
  if (!location || !isFiniteNumber(location.latitude) || !isFiniteNumber(location.longitude)) return undefined;
  return `${location.latitude}:${location.longitude}`;
};

const getAddressLines = (event?: EventLocationData): string[] => {
  const address = (event?.data?.location as { address?: unknown } | undefined)?.address;
  return Array.isArray(address) ? address.filter((line): line is string => typeof line === 'string' && line.trim().length > 0) : [];
};

const toAddressLines = (address?: unknown): string[] => {
  return Array.isArray(address) ? address.filter((line): line is string => typeof line === 'string' && line.trim().length > 0) : [];
};

const toAddressDisplayText = (addressLines: string[]): string => {
  const lastLine = addressLines[addressLines.length - 1]?.trim();
  const isCountryLine = Boolean(lastLine) && /^[A-Za-z][A-Za-z .'-]*$/.test(lastLine);
  const displayLines = isCountryLine ? addressLines.slice(0, -1) : addressLines;

  return displayLines.join(', ');
};

const toLocalityText = (addressLines: string[]): string => {
  if (!addressLines.length) return '';

  const localityLine = addressLines[1] ?? addressLines[0];
  return localityLine.replace(/,?\s+[A-Z]{2,3}\s+\d{3,4}$/u, '').trim();
};

export const formatAddressText = (event?: EventLocationData, unknownLabel = '--'): string => {
  const addressLines = getAddressLines(event);
  if (addressLines.length) {
    const addressText = toAddressDisplayText(addressLines);
    if (addressText) return addressText;
  }
  return formatLatLngText(event, unknownLabel);
};

export const resolveAddressText = async (
  event: Undefinable<EventLocationData>,
  reverseGeocode: ReverseGeocodeResolver,
  unknownLabel = '--',
): Promise<string> => {
  const existingAddressLines = getAddressLines(event);
  if (existingAddressLines.length) {
    const existingAddressText = toAddressDisplayText(existingAddressLines);
    if (existingAddressText) return existingAddressText;
  }

  const location = event?.data?.location;
  if (!location || !isFiniteNumber(location.latitude) || !isFiniteNumber(location.longitude)) {
    return formatLatLngText(event, unknownLabel);
  }

  try {
    const response = await reverseGeocode({ lat: location.latitude, long: location.longitude });
    const resolvedAddressLines = toAddressLines(response?.address);
    if (resolvedAddressLines.length) {
      const resolvedAddressText = toAddressDisplayText(resolvedAddressLines);
      if (resolvedAddressText) return resolvedAddressText;
    }
  } catch {
    return formatLatLngText(event, unknownLabel);
  }

  return formatLatLngText(event, unknownLabel);
};

export const createThrottledReverseGeocodeResolver = (
  reverseGeocode: ReverseGeocodeResolver,
  options: ThrottledReverseGeocodeResolverOptions = {},
): ReverseGeocodeResolver => {
  const intervalMs = options.intervalMs ?? 250;
  let queue = Promise.resolve();
  let lastRequestStartedAt = 0;

  return (params) => {
    const run = queue.then(async () => {
      const now = Date.now();
      const waitMs = Math.max(0, intervalMs - (now - lastRequestStartedAt));
      if (waitMs > 0) {
        await new Promise((resolve) => globalThis.setTimeout(resolve, waitMs));
      }

      lastRequestStartedAt = Date.now();
      return reverseGeocode(params);
    });

    queue = run.then(() => undefined, () => undefined);
    return run;
  };
};

export const resolveTimelineAddressText = async ({
  cache,
  enabled,
  event,
  inFlight,
  reverseGeocode,
  unknownLabel = '--',
}: ResolveTimelineAddressTextOptions): Promise<string> => {
  if (!enabled) return formatAddressText(event, unknownLabel);

  const coordinateKey = toCoordinateKey(event);
  if (!coordinateKey) return formatAddressText(event, unknownLabel);

  const cachedLabel = cache.get(coordinateKey);
  if (cachedLabel) return cachedLabel;

  const existingRequest = inFlight?.get(coordinateKey);
  if (existingRequest) return existingRequest;

  const request = resolveAddressText(event, reverseGeocode, unknownLabel).then((addressLabel) => {
    cache.set(coordinateKey, addressLabel);
    return addressLabel;
  }).finally(() => {
    inFlight?.delete(coordinateKey);
  });

  inFlight?.set(coordinateKey, request);
  return request;
};

export const formatSuburbOrLatLngText = (event?: EventLocationData, unknownLabel = '--'): string => {
  const addressLines = getAddressLines(event);
  if (!addressLines.length) return formatLatLngText(event, unknownLabel);

  const localityWithoutState = toLocalityText(addressLines);

  if (localityWithoutState) return localityWithoutState;
  return formatLatLngText(event, unknownLabel);
};

export const resolveSuburbOrLatLngText = async (
  event: Undefinable<EventLocationData>,
  reverseGeocode: ReverseGeocodeResolver,
  unknownLabel = '--',
): Promise<string> => {
  const existingAddressLines = getAddressLines(event);
  if (existingAddressLines.length) {
    const existingLocalityText = toLocalityText(existingAddressLines);
    if (existingLocalityText) return existingLocalityText;
  }

  const location = event?.data?.location;
  if (!location || !isFiniteNumber(location.latitude) || !isFiniteNumber(location.longitude)) {
    return formatLatLngText(event, unknownLabel);
  }

  try {
    const response = await reverseGeocode({ lat: location.latitude, long: location.longitude });
    const resolvedAddressLines = toAddressLines(response?.address);
    if (resolvedAddressLines.length) {
      const resolvedLocalityText = toLocalityText(resolvedAddressLines);
      if (resolvedLocalityText) return resolvedLocalityText;
    }
  } catch {
    return formatLatLngText(event, unknownLabel);
  }

  return formatLatLngText(event, unknownLabel);
};

export const buildTripRouteLabel = (
  trip: TripSummaryLike,
  events: EventLocationData[],
  unknownLabel: string,
  inProgressLabel: string,
  singleRouteLabel: string,
): string => {
  const tripEvents = getTripEventsInRange(trip, events);
  const startEvent = tripEvents[0];
  const finishEvent = tripEvents.length ? tripEvents[tripEvents.length - 1] : undefined;

  if (!startEvent) return trip.isOngoing ? inProgressLabel : singleRouteLabel;

  const startLabel = formatSuburbOrLatLngText(startEvent, unknownLabel);

  if (trip.isOngoing) return `${startLabel} -> ${inProgressLabel}`;

  if (!finishEvent) return singleRouteLabel;

  const finishLabel = formatSuburbOrLatLngText(finishEvent, unknownLabel);
  return `${startLabel} -> ${finishLabel}`;
};

export const buildTripRouteLabelWithReverseGeocode = async (
  trip: TripSummaryLike,
  events: EventLocationData[],
  reverseGeocode: ReverseGeocodeResolver,
  unknownLabel: string,
  inProgressLabel: string,
  singleRouteLabel: string,
): Promise<string> => {
  const tripEvents = getTripEventsInRange(trip, events);
  const startEvent = tripEvents[0];
  const finishEvent = tripEvents.length ? tripEvents[tripEvents.length - 1] : undefined;

  if (!startEvent) return trip.isOngoing ? inProgressLabel : singleRouteLabel;

  const startLabel = await resolveSuburbOrLatLngText(startEvent, reverseGeocode, unknownLabel);

  if (trip.isOngoing) return `${startLabel} -> ${inProgressLabel}`;

  if (!finishEvent) return singleRouteLabel;

  const finishLabel = await resolveSuburbOrLatLngText(finishEvent, reverseGeocode, unknownLabel);
  return `${startLabel} -> ${finishLabel}`;
};

export const buildTripRouteFullAddressLabel = async (
  trip: TripSummaryLike,
  events: EventLocationData[],
  reverseGeocode: ReverseGeocodeResolver,
  unknownLabel: string,
  tripInProgressLabel: string,
  singleRouteLabel: string,
): Promise<string> => {
  const tripEvents = getTripEventsInRange(trip, events);
  const startEvent = tripEvents[0];
  const finishEvent = tripEvents.length ? tripEvents[tripEvents.length - 1] : undefined;

  if (!startEvent) return trip.isOngoing ? tripInProgressLabel : singleRouteLabel;

  const startLabel = await resolveAddressText(startEvent, reverseGeocode, unknownLabel);

  if (trip.isOngoing) return `${startLabel} - ${tripInProgressLabel}`;

  if (!finishEvent) return singleRouteLabel;

  const finishLabel = await resolveAddressText(finishEvent, reverseGeocode, unknownLabel);
  return `${startLabel} - ${finishLabel}`;
};
